/*---------------------------------------------------------------------------------------------
 *  Copyright (c) by Solusek Systems. All rights reserved.
 *--------------------------------------------------------------------------------------------*/
 
 import React from "react";
import L from "../Lang"
import BaseCom from "../BaseCom";
import Icon from "../Icon";
import { withParamsAndNavigate } from "../WithParamsAndNavigate";
import './Chat.css';
import FormInput from "../FormInput";
import { qreq } from "../../shared/qrequest";
import Globals from "../../shared/Globals";
import Tick from "../Tick";
import { Link } from "react-router-dom";
import { Alert } from "react-bootstrap";
import { ImageGalleryOverlayGlobal } from "../ImageGalleryOverlay";

class ChatChannel extends BaseCom {
    constructor(props) {
        super(props);
        this.state = {
            chat: {},
            info: {},
            members: [],
            messages: [],
            attachments: [],
            attachmentsQueue: [],
            lastID: 0,
            canSend: false
        };
        this.bodyRef = React.createRef();
        this.headerRef = React.createRef();
        this.footerRef = React.createRef();
        this.composeRef = React.createRef();
        this.fileRef = React.createRef();
        this.load = this.load.bind(this);
        this.loadMessages = this.loadMessages.bind(this);
        this.keyDetect = this.keyDetect.bind(this);
        this.patientSelected = this.patientSelected.bind(this);
        this.createChannel = this.createChannel.bind(this);
    }

    componentDidMount() {
        // send
        document.addEventListener('keydown', this.keyDetect);

        // attach
        document.addEventListener('drop', this.onFileDrop);
        document.addEventListener('dragover', this.onFileDragOver);
        document.addEventListener('dragleave', this.onFileDragLeave);
        document.addEventListener('dragmove', this.onFileDragOver);
        
        if (this.props.params.id)
            this.load(this.props.params.id);
        if(this.props.params.orderID)
            this.createChannel(this.props.params.orderID);
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.props.params.id !== prevProps.params.id)
            this.load(this.props.params.id);
        if (this.state.lastID !== prevState.lastID)
            this.scrollBottom();
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.keyDetect);
        document.removeEventListener('drop', this.onFileDrop);
        document.removeEventListener('dragover', this.onFileDragOver);
        document.removeEventListener('dragleave', this.onFileDragLeave);
        document.removeEventListener('dragmove', this.onFileDragOver);
    }

    scrollBottom = (delay) => {
        if(!delay) delay = 1000;
        this.bodyRef.current.scrollTop = this.bodyRef.current.scrollHeight;
        setTimeout(() => {
            try
            {
                this.bodyRef.current.scrollTop = this.bodyRef.current.scrollHeight;
            } catch(e) {}
        }, delay);
    }

    load(id) {
        if (id === 'new')
            return;
        qreq.get('/api/chat/channel/get/' + id, j => {
            if (j.item) {
                this.setState({ chat: j.item });
                this.loadMessages(j.item);
                this.checkChatUserStatus(j.item.masterID);
            }
            else
                this.alert(j.errorMessage);
        }, this.unkownErrorCallback);
    }


    loadMessages(channel) {
        if (!channel.id)
            return;
        qreq.get('/api/chat/channel/' + channel.masterID + '/message/list', j => {
            if (j.list) {
                this.setState({ messages: j.list, lastID: j.list.length > 0 ? j.list[j.list.length - 1].id : 0 });
            }
            else
                this.alert(j.errorMessage);
        }, this.unkownErrorCallback);
    }


    loadMessagesInc(lastID) {
        if (!this.state.chat.id)
            return;
        if (!lastID) {
            this.loadMessages(this.state.chat);
            return;
        }
        if(this.loadMessagesInc_process)
            return;
        this.loadMessagesInc_process = true;
        qreq.get('/api/chat/channel/' + this.state.chat.masterID + '/message/list/inc?lastID=' + lastID, j => {
            if (j.list) {
                if (j.list.length > 0) {
                    var messages = [...this.state.messages].concat(j.list);
                    this.setState({ messages: messages, lastID: j.list.length > 0 ? j.list[j.list.length - 1].id : 0 });
                }
            }
            else
                this.alert(j.errorMessage);
            this.loadMessagesInc_process = false;
        }, () => {
            this.loadMessagesInc_process = false;
        }); /* no unknown error callback */
    }

    patientSelected(v) {
        qreq.get('/api/order/patient/' + v.id, j => {
            if (j.item) {
                this.createChannel(j.item.id);
            }
            else
                this.alert(j.errorMessage);
        }, this.unkownErrorCallback);
    }

    createChannel(orderID) {
        qreq.post('/api/chat/channel/create', { orderID: orderID }, j => {
            if (j.item) {
                this.props.navigate('/chat/' + j.item.id, { replace: true });
            }
            else if (j.errorCode === 9)
                this.props.navigate('/chat/' + j.errorMessage, { replace: true });
            else
                this.alert(j.errorMessage);
        }, this.unkownErrorCallback);
    }

    keyDetect(e) {
        if(this.state.composeFocus) {
            if ((e.keyCode === 13 || e.which === 13) && !e.shiftKey) {
                e.preventDefault();
                this.setState({ canSend: false });
                this.sendMessage(e.target.value, e.target);
                e.target.value = '';
            }
        }
    }

    triggerMessageSend = () => {
        this.setState({ canSend: false });
        this.sendMessage(this.composeRef.current.value, this.composeRef.current);
        this.composeRef.current.value = '';
    }

    sendMessage(message, senderElement) {
        if(!message)
            return;
        qreq.post('/api/chat/channel/' + this.state.chat.masterID + '/message/send', { chatChannelID: this.state.chat.id, message: message, attachments: [...this.state.attachments] }, j => {
            if (j.errorCode === 0) {
                this.loadMessagesInc(this.state.lastID);
                this.setState({ attachments: [] });
            }
            else {
                this.alert(j.errorMessage);
                if(senderElement) {
                    senderElement.value = message;
                    this.setState({ canSend: true });
                }
            }
        }, () => {
            this.alert('Failed to send message.');
            if(senderElement) {
                senderElement.value = message;
                this.setState({ canSend: true });
            }
        });
    }

    change = (e) => {
        this.setState({ canSend: this.composeRef.current.value.trim().length > 0 });
    }

    selectAttachment = () => {
        this.fileRef.current.click();
    }

    onFileDragOver = (e) => {
        e.preventDefault();
        if(this.dragLeaveTimeout)
            clearTimeout(this.dragLeaveTimeout);
        this.headerRef.current.classList.add('ChatNoDrop');
        this.bodyRef.current.classList.add('ChatNoDrop');
        this.footerRef.current.classList.add('ChatFileDrop');
    }

    onFileDragLeave = (e) => {
        e?.preventDefault();
        this.dragLeaveTimeout = setTimeout(() => {
            this.headerRef.current.classList.remove('ChatNoDrop');
            this.bodyRef.current.classList.remove('ChatNoDrop');
            this.footerRef.current.classList.remove('ChatFileDrop');
        }, 100);
    }

    onFileDrop = (e) => {
        e.preventDefault();
        if (e.dataTransfer && e.dataTransfer.items) {
            [...e.dataTransfer.items].forEach(i => {
                if (i.kind === "file") {
                    const file = i.getAsFile();
                    this.upload(file);
                }
            });
        }
    }

    onFileSelected = (e) => {
        this.upload(e.target.files[0]);
        e.target.value = '';
    }

    isImageFile = (file) => {
        return file.type.split('/')[0] === 'image';
    }
    
    isImageMime = (mime) => {
        return mime.split('/')[0] === 'image';
    }

    upload = (file) => {
        const formData = new FormData();
        formData.append('file', file);
        var currentCanSend = this.state.canSend;
        this.setState({ canSend: false, uploading: true, attachmentsQueue: [...this.state.attachmentsQueue, file] });
        qreq.postform('/api/chat/channel/' + this.state.chat.masterID + '/message/attach', formData, j => {
            if (j.item) {
                this.onFileDragLeave();
                j.item.__file = file;
                this.setState({ attachments: [...this.state.attachments, j.item], attachmentsQueue: this.state.attachmentsQueue.filter(i => i !== file) });
            }
            else
                this.alert(j.errorMessage);
            this.setState({ uploading: false, canSend: currentCanSend, attachmentsQueue: this.state.attachmentsQueue.filter(i => i !== file) });
        }, () => {
            this.alert('Failed to upload file.');
            this.setState({ uploading: false, canSend: currentCanSend, attachmentsQueue: this.state.attachmentsQueue.filter(i => i !== file) });
        }, { contentType: 'multipart/form-data' });
    }

    deleteAttachment = (attachment) => {
        qreq.post('/api/chat/channel/' + this.state.chat.masterID + '/message/detach', { id: attachment.id }, j => {
            if (j.errorCode === 0) {
                // do nothing
            }
            else
                this.alert(j.errorMessage);
        }, () => {
            this.alert('Failed to delete attachment.');
        });
        this.setState({ attachments: this.state.attachments.filter(i => i.id !== attachment.id) });
    }

    checkChatUserStatus = (channelMID) => {
        if(!channelMID) channelMID = this.state.chat.masterID;
        qreq.get('/api/chat/channel/' + channelMID + '/user/status', j => {
            if(j.list) {
                this.setState({ members: j.list });
            }
        });
    }

    showImage = (msg, img) => {
        let list = msg.attachments.filter(x => x.mimeType.split('/')[0] === 'image');
        if(list.length === 0)
            return;
        let index = list.findIndex(x => x.id === img.id);
        list.splice(index, 1);
        list.splice(0, 0, img);
        ImageGalleryOverlayGlobal.show(list);
    }

    checkUrlInternal = (url) => {
        if(url.startsWith('https://' + window.location.hostname))
            return true;
        return false;
    }

    processMessage = (msg) => {
        try {
            [...msg.matchAll(/(https?:\/\/[^\s]+)/g)].forEach(i => {
                if(this.checkUrlInternal(i[0]))
                    msg = msg.replace(i[0], '<a href="' + i[0] + '"><L>' + i[0] + '</L></a>');
                else
                    msg = msg.replace(i[0], '<a href="' + i[0] + '" onclick="javascript:return confirm(\'This link will send you to the external url\\n\\\'' + i[1] + '\\\'\\nare you sure you want to continue?\');" target="_blank"><L>' + i[0] + '</L></a>');
            });
        }
        catch{}
        try
        {
            [...msg.matchAll(/(\d{10})/g)].forEach(i => {
                msg = msg.replace(i[0], '<a href="tel:' + i[0] + '">' + i[0].substring(0, 3) + '-' + i[0].substring(3, 6) + '-' + i[0].substring(6, 10) + '</a>');
            });
        }
        catch{}
        try {
            [...msg.matchAll(/(\d{3})-(\d{3})-(\d{4})/g)].forEach(i => {
                msg = msg.replace(i[0], '<a href="tel:' + i[1] + i[2] + i[3] + '"><L>' + i[1] + '-' + i[2] + '-' + i[3] + '</L></a>');
            });
        }
        catch {}
        return this.processComment(msg);
    }

    render() {
        return <div className="container-fluid Chat">
            <input ref={this.fileRef} type="file" style={{ display: 'none' }} onChange={this.onFileSelected} />
            <Tick interval={5000} onTick={() => this.loadMessagesInc(this.state.lastID)} />
            <Tick interval={60000} onTick={() => this.checkChatUserStatus()} />
            <div className="ChatHeader" ref={this.headerRef}>
                <div className="d-flex align-items-center">
                    <div>
                        <button className="btn btn-secondary rounded me-2" onClick={this.goBack}><Icon icon="faArrowLeft" /></button>
                    </div>
                    <div className="flex-fill fw-bold">
                        {this.state.chat.id ? this.state.chat.name : <FormInput model={this.state.info} name="patientID" label="Patient name search..." type="search" searchUrl="/api/patient/list" inputGroupClassName="mb-0" labelAsPlaceholder required onChange={this.patientSelected} />}
                    </div>
                    {this.state.chat.orderID ? <div className="text-end">
                        <Link to={'/order/view/' + this.state.chat.orderID} className="btn btn-primary rounded ms-1"><Icon icon="faInfoCircle" /></Link>
                    </div> : <div className="text-end">
                        <Link to={`/chat/${this.state.chat.id}/info`} className="btn btn-primary rounded ms-1"><Icon icon="faInfoCircle" /></Link>
                    </div>}
                </div>
            </div>
            <div className="ChatBody" ref={this.bodyRef}>
                {!this.state.chat.id && <Alert className="mt-1"><L>Please select a patient above to start a chat about.</L></Alert>}
                {this.state.messages && this.state.messages.map(i => <div key={i.id} className="ChatMessage">
                    <div className="ChatMessageHeader">
                        <div className="d-flex align-items-center">
                            <div className="fw-bold">{i.userName} {this.state.members.find(a => a.userID === i.userID)?.isOnline && <> <Icon icon="faGlobe" className="text-success" title="Is Online" /></>}</div>
                            <div className="flex-fill text-end text-muted">{Globals.toMDYHMLocalDateStringOrDiff(i.createdDate)}</div>
                        </div>
                    </div>
                    <div className="ChatMessageBody">
                        <p dangerouslySetInnerHTML={{__html: this.processMessage(i.message)}}></p>
                    </div>
                    {i.attachments && <div className="ChatMessageAttachments d-flex">
                        {i.attachments.map(a => <div key={a.id} className="ChatAttachment text-center d-flex flex-column align-items-center justify-content-center">
                            <span role="button" onClick={() => this.showImage(i, a)} className="d-block flex-fill">
                                {this.isImageMime(a.mimeType) ? <img src={Globals.getImageURL(a.url)} alt="" className="img-fluid" style={{maxWidth:'100px'}} />
                                 : <Icon icon="faFile" className="d-block flex-fill p-1" />}
                            </span>
                            <a href={Globals.getImageURL(a.url)} target="_blank" rel="noreferrer" className="d-block text-center">{a.displayFileName}</a>
                        </div>)}
                    </div>}
                </div>)}
            </div>
            {(this.state.attachments?.length || this.state.attachmentsQueue?.length) && <div className="ChatFooterAttach d-flex align-items-stretch">
                {this.state.attachmentsQueue.map(i => <div key={i.name} className="ChatAttachment text-center d-flex flex-column">
                    <Icon icon="faSpinner" />
                    <span>{i.name}</span>
                </div>)}

                {this.state.attachments.map(i => <div key={i.id} className="ChatAttachment text-center d-flex flex-column overflow-hidden text-center">
                    <div className="ChatAttachment-delete" role="button" onClick={() => this.deleteAttachment(i)}><Icon icon="faTimes" /></div>
                    {this.isImageFile(i.__file) ? <img src={URL.createObjectURL(i.__file)} alt="" className="img-fluid" style={{maxWidth:'100%', height: '100px'}} /> : <Icon icon="faFile" className="d-block flex-fill p-1" />}
                    <span>{i.displayFileName}</span>
                </div>)}
            </div>}
            <div className="ChatFooter" ref={this.footerRef}>
                <div className="d-flex align-items-stretch">
                    <button className="btn btn-secondary d-block px-3" onClick={() => this.selectAttachment()}>
                        <Icon icon="faPaperclip" />
                    </button>
                    <div className="flex-fill"><textarea onFocus={() => this.setState({ composeFocus: true })} onBlur={() => this.setState({ composeFocus: false })} onChange={() => this.change()} disabled={!this.state.chat.id} ref={this.composeRef} className="form-control" placeholder="Aa" style={{ resize: 'none' }}></textarea></div>
                    <button className="btn btn-success d-block px-3" onClick={() => this.triggerMessageSend()} disabled={!this.state.canSend}>
                        <Icon icon="faPaperPlane" />
                    </button>
                </div>
            </div>
        </div>;
    }
}


export default withParamsAndNavigate(ChatChannel);